import { Injectable, inject } from '@angular/core';
import { Observable, from, map } from 'rxjs';

import { SupabaseService } from './supabase.service';

/**
 * A reusable, non-personalised public booking link (`crm.public_booking_links`).
 *
 * Unlike `crm.booking_tokens` — which names one patient or lead and is consumed
 * once — a row here is a per-branch credential that anyone may use: a QR code in
 * reception, the clinic's website, an Instagram bio. The slug IS the whole
 * credential and carries no ids, so the branch is resolved server-side and the
 * URL cannot be edited into another clinic's diary.
 */
export interface PublicBookingLinkRow {
  id: number;
  slug: string;
  branches_id: number;
  /** null = the public page asks the visitor which doctor; set = pinned. */
  doctor_id: number | null;
  /** Staff-facing only, never rendered to the patient. Defaults to '' server-side, never null. */
  label: string;
  is_active: boolean;
  campaign_id: number | null;
  qr_code_id: number | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  created_at: string;
  disabled_at: string | null;
}

export interface CreatePublicBookingLinkResult {
  ok: boolean;
  /**
   * 'created' on success. On failure one of 'forbidden' | 'unknown_doctor' |
   * 'unknown_campaign' | 'unknown_qr' | 'too_many_links'.
   */
  reason?: string;
  id?: number;
  slug?: string;
}

export interface RotatePublicBookingLinkResult {
  ok: boolean;
  /** 'rotated' on success; 'unknown' | 'forbidden' on failure. */
  reason?: string;
  /** The NEW row's id/slug — not the one that was rotated away. */
  id?: number;
  slug?: string;
  replaced_id?: number;
}

/**
 * Staff-side management of `crm.public_booking_links`.
 *
 * MINTING AND ROTATION GO THROUGH RPCs, NOT THROUGH THE TABLE, and that is not a
 * stylistic choice: `authenticated` holds SELECT plus a COLUMN-SCOPED UPDATE on
 * this table and has neither INSERT nor DELETE.
 *
 *  - No INSERT, because `slug` has a default but an INSERT can override one — a
 *    staff member or a stolen session could mint `slug = 'metasmile'` and create
 *    a permanently guessable endpoint that writes into the clinic's diary. The
 *    RPC never accepts a slug at all.
 *  - No DELETE, because `crm.booking_requests.public_booking_link_id` points
 *    here: deleting a row destroys the attribution history this feature exists
 *    to produce. Deactivation (below) is the only removal.
 *
 * So do not "simplify" create/rotate into `.insert()` calls — they will fail,
 * and the failure is the table defending itself.
 *
 * `.schema('crm')` throughout, matching BookingSettingsService and
 * BookingRequestService: these objects live in the CRM app's database, shared
 * through Postgres rather than through code.
 */
@Injectable({ providedIn: 'root' })
export class PublicBookingLinkService {
  private supabase = inject(SupabaseService);

  /**
   * Every link for a branch, active first and newest first within each group —
   * so a freshly minted or rotated link lands at the top where staff is looking,
   * and deactivated history sinks below without disappearing.
   *
   * RLS already scopes this to branches the user can manage
   * (`crm.can_manage_booking_settings`); the explicit branch filter is for the
   * multi-branch case, where the user may legitimately manage several.
   */
  getLinks(branchId: number): Observable<PublicBookingLinkRow[]> {
    return from(
      this.supabase.client
        .schema('crm')
        .from('public_booking_links')
        .select(
          'id, slug, branches_id, doctor_id, label, is_active, campaign_id, qr_code_id, utm_source, utm_medium, utm_campaign, created_at, disabled_at',
        )
        .eq('branches_id', branchId)
        .order('is_active', { ascending: false })
        .order('created_at', { ascending: false }),
    ).pipe(
      map((response: any) => {
        if (response.error) throw response.error;
        return (response.data as PublicBookingLinkRow[]) || [];
      }),
    );
  }

  /**
   * Mints a link. The cap is 50 ACTIVE links per branch, answered as
   * `ok:false, reason:'too_many_links'` rather than a thrown error — check
   * `.ok`/`.reason` on the resolved value, every failure here arrives that way.
   *
   * `doctorId` is validated server-side against `crm.public_bookable_doctors`,
   * NOT against the branch's full doctor list: pinning a doctor who is not
   * publicly bookable (excluded by the booking-settings allowlist, or with no
   * availability) answers 'unknown_doctor', because a printed QR pointing at an
   * empty grid is worse than no QR.
   */
  createLink(
    branchId: number,
    doctorId: number | null,
    label: string | null,
  ): Observable<CreatePublicBookingLinkResult> {
    return from(
      this.supabase.client.schema('crm').rpc('create_public_booking_link', {
        p_branches_id: branchId,
        p_doctor_id: doctorId ?? null,
        p_label: label ?? null,
        // Campaign / QR-code / UTM attribution is settable server-side but has
        // no UI yet. Passed explicitly as null rather than omitted so the call
        // keeps binding the same overload if defaults ever change.
        p_campaign_id: null,
        p_qr_code_id: null,
        p_utm_source: null,
        p_utm_medium: null,
        p_utm_campaign: null,
      }),
    ).pipe(
      map((response: any) => {
        if (response.error) throw response.error;
        return response.data as CreatePublicBookingLinkResult;
      }),
    );
  }

  /**
   * Mints a replacement carrying the old row's label, pinned doctor and
   * attribution, and deactivates the old row in the SAME transaction.
   *
   * The returned `id`/`slug` are the NEW link; `replaced_id` is the one that was
   * just retired. The old slug stops resolving immediately — see the warning
   * copy in booking-settings-tab.html.
   */
  rotateLink(id: number): Observable<RotatePublicBookingLinkResult> {
    return from(
      this.supabase.client.schema('crm').rpc('rotate_public_booking_link', {
        p_id: id,
      }),
    ).pipe(
      map((response: any) => {
        if (response.error) throw response.error;
        return response.data as RotatePublicBookingLinkResult;
      }),
    );
  }

  /**
   * Retires a link. A plain UPDATE, not an RPC: `is_active` and `disabled_at`
   * are both inside the column-scoped grant, and RLS scopes the row to the
   * user's own branch, so nothing extra is needed.
   *
   * There is no reactivation counterpart, deliberately: a slug that has been out
   * of service is a slug whose printed copies are of unknown provenance, and
   * minting a fresh one costs nothing.
   */
  deactivateLink(id: number): Observable<void> {
    return from(
      this.supabase.client
        .schema('crm')
        .from('public_booking_links')
        .update({ is_active: false, disabled_at: new Date().toISOString() })
        .eq('id', id),
    ).pipe(
      map((response: any) => {
        if (response.error) throw response.error;
      }),
    );
  }
}
